import React, { useEffect, useState } from "react";
import { api } from "./api";

const FALLBACK = {
  product: "LucidGate",
  version: process.env.REACT_APP_VERSION || "0.0.0",
  date: "",
  title: "",
  highlights: [],
  runtime: null,
};

const UNKNOWN = "onbekend";

function cleanMeta(value, maxLen) {
  if (value == null) return null;
  const text = String(value).trim();
  if (!text) return null;
  const lower = text.toLowerCase();
  if (text.includes("://") || lower.startsWith("mongodb:") || lower.startsWith("file:")) return null;
  if (text.startsWith("/") || text.startsWith("~") || text.startsWith("\\")) return null;
  return text.slice(0, maxLen);
}

function dirtyFromEnv(raw) {
  if (raw == null || raw === "") return null;
  const v = String(raw).trim().toLowerCase();
  if (["1", "true", "yes", "dirty"].includes(v)) return true;
  if (["0", "false", "no", "clean"].includes(v)) return false;
  return null;
}

const SHA_RE = /^[0-9a-fA-F]{7,40}$/;

/** Keep full hex SHA for comparison; invalid → null. */
export function cleanGitSha(value) {
  if (value == null) return null;
  const text = String(value).trim().toLowerCase();
  if (!text || !SHA_RE.test(text)) return null;
  return text;
}

/** Frontend identity from CRA build/start env only (never from backend payload). */
export function readFrontendRuntimeIdentity() {
  return {
    env_label: cleanMeta(process.env.REACT_APP_LG_ENV_LABEL, 80),
    git_branch: cleanMeta(process.env.REACT_APP_LG_GIT_BRANCH, 120),
    git_sha: cleanGitSha(process.env.REACT_APP_LG_GIT_SHA),
    git_dirty: dirtyFromEnv(process.env.REACT_APP_LG_GIT_DIRTY),
    role: "frontend",
  };
}

export function displayMeta(value) {
  if (value == null || value === "") return UNKNOWN;
  if (typeof value === "boolean") return value ? "dirty" : "clean";
  return String(value);
}

/** Presentation only: shorten SHA for UI; full value stays in identity/compare. */
export function displayGitSha(sha) {
  if (sha == null || sha === "") return UNKNOWN;
  const text = String(sha);
  return text.length > 12 ? text.slice(0, 12) : text;
}

/**
 * Code-identity compare (SHA only). Branch is display context, not a mismatch.
 * - mismatch: both SHAs present and differ
 * - unknown: missing SHA on either side, or dirty on either side (working tree not proven equal)
 * - match: same full SHA and both sides clean
 */
export function runtimeIdentityCompare(frontend, backend) {
  if (!frontend || !backend) return "unknown";
  const a = frontend.git_sha;
  const b = backend.git_sha;
  if (!a || !b) return "unknown";
  if (a !== b) return "mismatch";
  if (frontend.git_dirty === true || backend.git_dirty === true) return "unknown";
  if (frontend.git_dirty !== false || backend.git_dirty !== false) return "unknown";
  return "match";
}

/** True only when both sides have a SHA and they differ. */
export function runtimeIdentityMismatch(frontend, backend) {
  return runtimeIdentityCompare(frontend, backend) === "mismatch";
}

/**
 * Load release info: API first, then /release.json (public), then env fallback.
 * Preserves product fields; attaches backend runtime from API when present.
 */
export function useAppVersion() {
  const [info, setInfo] = useState(FALLBACK);

  useEffect(() => {
    let cancelled = false;

    const apply = (data) => {
      if (cancelled || !data?.version) return;
      setInfo({
        product: data.product || "LucidGate",
        version: String(data.version),
        date: data.date || "",
        title: data.title || "",
        highlights: Array.isArray(data.highlights) ? data.highlights : [],
        runtime: data.runtime && typeof data.runtime === "object" ? data.runtime : null,
      });
    };

    api
      .version()
      .then(apply)
      .catch(() =>
        fetch(`${process.env.PUBLIC_URL || ""}/release.json`)
          .then((r) => (r.ok ? r.json() : Promise.reject()))
          .then(apply)
          .catch(() => {
            if (!cancelled) setInfo(FALLBACK);
          })
      );

    return () => {
      cancelled = true;
    };
  }, []);

  return info;
}

/** Compact always-visible product version label. */
export function AppVersionBadge({ className = "", showDate = true, testId = "app-version-badge" }) {
  const info = useAppVersion();
  return (
    <span
      data-testid={testId}
      className={
        className ||
        "font-mono text-[11px] uppercase tracking-widest text-slate-500"
      }
      title={info.title || `LucidGate ${info.version}`}
    >
      v{info.version}
      {showDate && info.date ? ` · ${info.date}` : ""}
    </span>
  );
}

const BREAK_LINE =
  "min-w-0 break-words [overflow-wrap:anywhere] [word-break:break-word]";

/**
 * Shared product + runtime block for sidebar, header popover, and welcome-gate.
 * Product (SemVer/release date) stays visually separate from runtime identity.
 */
export function VersionRuntimeDetails({
  info,
  frontend = null,
  includeProduct = true,
  testId = "version-runtime-details",
  className = "",
}) {
  const fe = frontend || readFrontendRuntimeIdentity();
  const be = info?.runtime && typeof info.runtime === "object" ? info.runtime : null;
  const mismatch = runtimeIdentityMismatch(fe, be);
  const version = info?.version != null && info.version !== "" ? String(info.version) : UNKNOWN;
  const date = info?.date ? String(info.date) : "";

  return (
    <div
      data-testid={testId}
      className={
        className ||
        `min-w-0 space-y-2 font-mono text-[10px] leading-relaxed tracking-wide text-slate-500`
      }
    >
      {includeProduct ? (
        <div data-testid={`${testId}-product`} className="min-w-0 space-y-0.5">
          <div className="uppercase tracking-widest text-slate-600">Product</div>
          <div data-testid="preview-product-line" className={BREAK_LINE}>
            v{version}
            {date ? ` · release ${date}` : ""}
          </div>
        </div>
      ) : null}
      <div
        data-testid={`${testId}-runtime`}
        className={`min-w-0 space-y-0.5${includeProduct ? " border-t border-subtle/50 pt-2" : ""}`}
      >
        <div className="uppercase tracking-widest text-slate-600">Runtime</div>
        <div data-testid="preview-env-line" className={BREAK_LINE}>
          Omgeving {displayMeta(fe.env_label || be?.env_label)}
        </div>
        <div data-testid="preview-fe-line" className={BREAK_LINE} title={fe.git_sha || undefined}>
          Frontend {displayMeta(fe.git_branch)} @ {displayGitSha(fe.git_sha)}
          {fe.git_dirty == null ? "" : ` · ${displayMeta(fe.git_dirty)}`}
        </div>
        <div data-testid="preview-be-line" className={BREAK_LINE} title={be?.git_sha || undefined}>
          Backend {displayMeta(be?.git_branch)} @ {displayGitSha(be?.git_sha)}
          {be?.git_dirty == null ? "" : ` · ${displayMeta(be.git_dirty)}`}
        </div>
        {mismatch ? (
          <div data-testid="preview-runtime-mismatch" className={`${BREAK_LINE} text-warn`}>
            Frontend en backend commit komen niet overeen
          </div>
        ) : null}
      </div>
    </div>
  );
}

/** Product + runtime via shared VersionRuntimeDetails (FE env + BE API runtime). */
export function PreviewRuntimeBadge({ className = "", testId = "preview-runtime-badge" }) {
  const info = useAppVersion();
  return <VersionRuntimeDetails info={info} testId={testId} className={className} />;
}
